import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import FolderIcon from 'dibs-vg/dist/react/folder';
import FolderComponent from 'dibs-portfolio-folder/src/components/DirectToFolderWrapper';
import HeartWrapper from 'dibs-portfolio-heart';
import HeartOutlinedIcon from 'dibs-vg/dist/react/heart-outlined';
import TagIcon from 'dibs-vg/dist/react/tag-empty';
import { Button } from 'dibs-elements/exports/Button';

// Helpers
import { localStorage } from 'dibs-browser-storage';
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import get from 'lodash.get';
import { copy, cutoffs, mutations, tabs } from '../../constants';

// Styles
import styles from './styles/Recommendations.scss';

// Tracking
import { getFolderTrackingHooks, locations } from '../../../utils/folderTracking';
import { trackEvent } from 'dibs-tracking';

class Recommendations extends React.Component {
    constructor() {
        super();

        this.state = {
            favoriteItem: false,
            selectedItemId: '',
            items: [],
            follows: [],
        };

        this.editItem = this.editItem.bind(this);
        this.authModalShow = this.authModalShow.bind(this);
        this.getRecentlyViewedItems = this.getRecentlyViewedItems.bind(this);
        this.getRecommendedItems = this.getRecommendedItems.bind(this);
        this.getTrendingCategoryItems = this.getTrendingCategoryItems.bind(this);
        this.getTrendingAttributeItems = this.getTrendingAttributeItems.bind(this);
        this.onCreateFolderClick = this.onCreateFolderClick.bind(this);
        this.onHeartClick = this.onHeartClick.bind(this);
        this.trackFavoriting = this.trackFavoriting.bind(this);
    }

    componentDidMount() {
        // Need to do this check for tests
        if (typeof require.ensure === 'function') {
            require.ensure(['dibs-buyer-layout/src/utils/AuthModalLoader'], () => {
                this.setState({
                    authModalLoader: require('dibs-buyer-layout/src/utils/AuthModalLoader').default,
                });
            });
        }

        this.getRecommendedItems();
        this.getFollowingItems();
    }

    authModalShow(itemId) {
        const { authModalLoader } = this.state;
        authModalLoader
            .show({
                action: authModalLoader.constants.LOGIN,
                flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                ga: {
                    label: 'save item - favorites page',
                },
            })
            .then(() => {
                this.props.setFavoriteItemOnLoad(true);
                this.props.selectItems([{ itemId }]);
            });
    }

    getRecentlyViewedItems() {
        if (localStorage.getItem('recent_history_list')) {
            return localStorage
                .getItem('recent_history_list')
                .slice(0, cutoffs.RECOMMENDATION_ITEMS);
        }
        return [];
    }

    getRecommendedItems() {
        const recentlyViewedItems = this.getRecentlyViewedItems();
        this.setState({ items: recentlyViewedItems });
    }

    getTrendingCategoryItems(numItems) {
        const trendingCategories = get(this.props, 'trending.trendingCategories') || [];
        return trendingCategories.slice(0, numItems) || [];
    }

    getTrendingAttributeItems(numItems) {
        const trendingAttributes = get(this.props, 'trending.trendingAttributes') || [];
        return trendingAttributes.slice(0, numItems);
    }

    getFollowingItems() {
        const categorieItems = this.getTrendingCategoryItems(2);
        this.setState({
            follows: categorieItems.concat(
                this.getTrendingAttributeItems(4 - categorieItems.length)
            ),
        });
    }

    editItem(type = tabs.FOLDER) {
        if (type === tabs.ITEMS) {
            this.props.setFullPageLoading(true);
            if (this.props.favoriteItemOnLoad) {
                this.props.setFavoriteItemOnLoad(false);
            }
        }
        this.trackFavoriting();
        this.props.resetUiState();
        this.props.setMutationResponse(mutations.responses.NULL);
    }

    onHeartClick(addToHeart) {
        addToHeart({ portfolioId: this.props.portfolioId, userId: this.props.userId });
    }

    onCreateFolderClick() {
        const { authModalLoader } = this.state;
        if (this.props.userId) {
            this.props.setCreateFolderOnLoad(true);
        } else {
            authModalLoader
                .show({
                    action: authModalLoader.constants.LOGIN,
                    flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                    ga: {
                        label: 'create folder - favorites page',
                    },
                    shouldPauseAuthFlow: state => state.step === 0,
                })
                .then(() => {
                    this.props.setCreateFolderOnLoad(true);
                });
        }
    }

    trackFavoriting() {
        trackEvent({
            action: 'item added',
            label: 'my favorites',
            category: 'item favoriting',
        });
    }

    render() {
        const {
            favoriteItemOnLoad,
            onCreateProjectClick,
            portfolioId,
            tab,
            trending,
            selectedItemIds,
            userId,
        } = this.props;
        const iconClasses = classnames(styles.main, {
            [styles.heart]: tab === tabs.ITEMS,
            [styles.folder]: tab === tabs.FOLDER || tab === tabs.FOLDERS || tabs === tabs.PROJECT,
            [styles.tag]: tab === tabs.PROJECTS,
            [styles.following]: tab === tabs.FOLLOWING,
        });

        let itemElements;
        let itemIcon;
        if (
            this.state.items.length &&
            (tab === tabs.ITEMS || tab === tabs.FOLDER || tab === tabs.PROJECT)
        ) {
            const selectedItemId =
                selectedItemIds && selectedItemIds.length ? selectedItemIds[0] : void 0;

            itemElements = this.state.items.map(item => {
                const forceFavoriteItem = !!favoriteItemOnLoad && selectedItemId === item.itemId;

                return (
                    <div
                        key={item.itemId}
                        className={`colXs6 colMd3 ${styles.itemContainer}`}
                        data-tn="recommended-item"
                    >
                        {tab === tabs.ITEMS ? (
                            <HeartWrapper
                                authModalShow={this.authModalShow.bind(this, item.itemId)}
                                forceFavoriteItem={forceFavoriteItem}
                                itemId={item.itemId}
                                onFavorited={() => this.editItem(tabs.ITEMS)}
                                onUnFavorited={() => this.editItem(tabs.ITEMS)}
                                onError={() => this.editItem(tabs.ITEMS)}
                                onHeartClick={this.onHeartClick.bind(this)}
                                userId={userId}
                                viewer={trending}
                            />
                        ) : (
                            <FolderComponent
                                onAddItemSuccess={this.editItem}
                                onAddItemError={this.editItem}
                                portfolioId={portfolioId || null}
                                primaryItemPk={item.itemId}
                                relay={this.props.relay}
                                trackingHooks={getFolderTrackingHooks(locations.MY_FAVORITES)}
                                userId={userId}
                                viewer={trending}
                            />
                        )}
                        <div key={item.itemId} className={styles.item}>
                            <a href={item.itemPdpUrl}>
                                <img
                                    src={get(item.itemImageUrl, 'src') || item.itemImageUrl}
                                    className={styles.itemImage}
                                />
                            </a>
                        </div>
                    </div>
                );
            });
        } else if (tab === tabs.FOLDERS) {
            itemElements = (
                <Button
                    dataTn="create-folder"
                    customClass="favorites"
                    onClick={this.onCreateFolderClick}
                >
                    {copy.bulkActions.CREATE_FOLDER}
                </Button>
            );
        } else if (tab === tabs.FOLLOWING) {
            itemElements = this.state.follows.map(item => {
                return (
                    <div key={item.url} className={`colXs6 colMd3 ${styles.linkContainer}`}>
                        <a href={item.url}>{item.displayName}</a>
                    </div>
                );
            });
        } else if (tab === tabs.PROJECTS) {
            itemElements = (
                <Button
                    dataTn="create-project-button-recommendations"
                    className="favorites"
                    onClick={onCreateProjectClick}
                >
                    {copy.bulkActions.CREATE_PROJECT}
                </Button>
            );
        }

        if (tab === tabs.ITEMS) {
            itemIcon = <HeartOutlinedIcon />;
        } else if (tab === tabs.FOLDERS) {
            itemIcon = <FolderIcon />;
        } else if (tab === tabs.FOLLOWING) {
            itemIcon = (
                <Button className={styles.button} type="secondary">
                    {copy.recommendations.FOLLOW}
                </Button>
            );
        } else if (tab === tabs.PROJECTS) {
            itemIcon = <TagIcon />;
        }

        const containerClass =
            tab === tabs.PROJECT ? styles.emptyProjectContainer : styles.container;
        const hasItems = itemElements && !!itemElements.length;
        return (
            <div className={containerClass}>
                <div className={iconClasses}>{itemIcon}</div>
                <div className={styles.title}>
                    {copy.recommendations[`${tab.toUpperCase()}_TITLE`]}
                </div>
                <div className={styles.description}>
                    {copy.recommendations[`${tab.toUpperCase()}_DESCRIPTION`]}
                </div>
                {[tabs.FOLDERS, tabs.PROJECTS].includes(tab) && (
                    <div className={styles.createFolder}>{itemElements}</div>
                )}
                {[tabs.FOLDER, tabs.ITEMS].includes(tab) && hasItems && (
                    <div className={styles.recommendationsContainer}>
                        <div className={`${styles.favoriting} colXs12`}>
                            {copy.recommendations[`${tab.toUpperCase()}_START`]}
                        </div>
                        {itemElements}
                    </div>
                )}
            </div>
        );
    }
}

Recommendations.propTypes = {
    favoriteItemOnLoad: PropTypes.bool,
    hasUserId: PropTypes.bool,
    onCreateProjectClick: PropTypes.func,
    portfolioId: PropTypes.string,
    relay: PropTypes.object,
    resetUiState: PropTypes.func,
    selectedItems: PropTypes.array,
    selectItems: PropTypes.func,
    setCreateFolderOnLoad: PropTypes.func,
    setFavoriteItemOnLoad: PropTypes.func,
    selectedItemIds: PropTypes.array,
    setFullPageLoading: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setUserHasChanged: PropTypes.func,
    tab: PropTypes.string,
    totalItems: PropTypes.number,
    trending: PropTypes.object,
    userId: PropTypes.string,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        tab: state.favorites.uiState.tab,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setCreateFolderOnLoad: createFolderOnLoad => {
            dispatch(actions.setCreateFolderOnLoad({ createFolderOnLoad }));
        },
        setFavoriteItemOnLoad: favoriteItemOnLoad => {
            dispatch(actions.setFavoriteItemOnLoad({ favoriteItemOnLoad }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setUserHasChanged: userHasChanged => {
            dispatch(actions.setUserHasChanged({ userHasChanged }));
        },
        setItemLoggedOutId: itemLoggedOutId => {
            dispatch(actions.setItemLoggedOutId({ itemLoggedOutId }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
    };
};

Recommendations = createFragmentContainer(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Recommendations),
    {
        trending: graphql`
            fragment Recommendations_trending on Viewer {
                ...HeartWrapper_viewer
                    @arguments(
                        loadPortfolioData: $loadPortfolioData
                        selectedItemIds: $selectedItemIds
                        userIds: $userIds
                    )
                trendingCategories: trending(trendType: CATEGORY, numItems: 2, categoryLevels: 3) {
                    displayName
                    url
                }
                trendingAttributes: trending(trendType: ATTRIBUTE, numItems: 2) {
                    displayName
                    url
                }
            }
        `,
    }
);

// For Testing
export { Recommendations as Raw };
export default Recommendations;
